import { computed } from 'vue';
import type { Project } from '@/types';

export function useProjectProgress(getProject: () => Project) {
    const identified = computed(() => getProject().papers_count ?? 0);
    const included = computed(() => getProject().papers_included_count ?? 0);
    const excluded = computed(() => getProject().papers_excluded_count ?? 0);
    const pending = computed(() => getProject().papers_pending_count ?? 0);
    const screened = computed(() => included.value + excluded.value);

    const selectionProgress = computed(() =>
        identified.value === 0
            ? 0
            : Math.round((screened.value / identified.value) * 100),
    );

    const steps = computed(() => [
        {
            num: 1,
            title: 'Identification',
            meta:
                identified.value === 0
                    ? 'Aucun article importé — commencez ici'
                    : `${identified.value} article${identified.value !== 1 ? 's' : ''} importé${identified.value !== 1 ? 's' : ''}`,
            done:
                identified.value > 0 &&
                pending.value === 0 &&
                screened.value > 0,
            current:
                identified.value === 0 ||
                (identified.value > 0 &&
                    pending.value > 0 &&
                    screened.value === 0),
            progress: null,
        },
        {
            num: 2,
            title: 'Sélection — titres et résumés',
            meta: `${screened.value} décision${screened.value !== 1 ? 's' : ''} prise${screened.value !== 1 ? 's' : ''} · ${pending.value} en attente`,
            done: pending.value === 0 && screened.value > 0,
            current: pending.value > 0 && screened.value > 0,
            progress: identified.value > 0 ? selectionProgress.value : null,
        },
        {
            num: 3,
            title: 'Éligibilité — texte intégral',
            meta: `${included.value} article${included.value !== 1 ? 's' : ''} évalué${included.value !== 1 ? 's' : ''} sur ${screened.value}`,
            done: false,
            current: screened.value > 0 && included.value > 0,
            progress:
                screened.value > 0
                    ? Math.round((included.value / screened.value) * 100)
                    : null,
        },
        {
            num: 4,
            title: 'Extraction des données',
            meta: `${included.value} article${included.value !== 1 ? 's' : ''} inclus`,
            done: false,
            current: false,
            progress: null,
        },
        {
            num: 5,
            title: 'Rédaction de la synthèse',
            meta: 'En attente',
            done: false,
            current: false,
            progress: null,
        },
    ]);

    const nextStepKey = computed(
        (): 'import' | 'screen' | 'extract' | 'write' => {
            if (identified.value === 0) {
                return 'import';
            }

            if (pending.value > 0) {
                return 'screen';
            }

            if (included.value > 0) {
                return 'extract';
            }

            return 'write';
        },
    );

    return {
        identified,
        included,
        excluded,
        pending,
        screened,
        selectionProgress,
        steps,
        nextStepKey,
    };
}
